/**
 * Pre-sedation information letter — interactive form for populating
 * arrival time and date before generating the PDF.
 */
import { SNOOTS_BG } from '@snootsvet/shared'

import { DatePicker } from '@/frontend/components/date-picker'
import { TimePicker } from '@/frontend/components/time-picker'
import { ClinicHeaderPreview } from '@/frontend/features/printing/documents/blocks/clinic-header'
import { DocumentPreview } from '@/frontend/features/printing/documents/blocks/document-preview'
import {
  ARRIVAL_NOTE,
  afterProcedureText,
  COST_NOTE,
  closingText,
  contactInfoText,
  defaultPreSedationLetterFields,
  FASTING_INSTRUCTIONS,
  fastingIntro,
  introText,
  type PreSedationLetterFields,
  PreSedationLetterPdf,
  pickUpText,
  shavedAreaText,
} from '@/frontend/features/printing/documents/pre-sedation-letter.print'
import type { DocumentFormProps } from '@/frontend/features/printing/documents/types'
import { withDocumentEditor } from '@/frontend/features/printing/documents/with-document-editor'

function PreSedationLetterForm({
  context,
  value,
  onChange,
}: DocumentFormProps<PreSedationLetterFields>) {
  const { patient, owner, clinic } = context

  return (
    <DocumentPreview>
      <ClinicHeaderPreview clinic={clinic} />

      <p className="mt-4">Dear {owner.firstName},</p>

      <p className="mt-3">{introText(patient.name)}</p>

      <p className="mt-4 font-semibold">The procedure will take place at {clinic.address}.</p>

      <p className="mt-3 flex flex-wrap items-center gap-1">
        <span className="font-semibold">Please arrive at</span>
        <TimePicker
          className="inline-flex w-28"
          placeholder="time"
          value={value.arrivalTime || undefined}
          onChange={(t) => onChange({ ...value, arrivalTime: t })}
          min="07:00"
          max="17:00"
          snap={5}
        />
        <span className="font-semibold">on the</span>
        <DatePicker
          value={value.appointmentDate ?? undefined}
          onChange={(d) => onChange({ ...value, appointmentDate: d ?? null })}
          placeholder="date"
          className="w-48"
        />
        <span className="font-semibold">for your appointment.</span>
      </p>
      <p className="font-semibold">{ARRIVAL_NOTE}</p>

      <h2 className="text-headline-md">What You Need to Know:</h2>

      <h3 className="text-headline-sm">1. Fasting Instructions</h3>
      <ul className="mt-1 list-disc pl-6">
        <li>{fastingIntro(patient.name)}</li>
        {FASTING_INSTRUCTIONS.map((item) => (
          <li key={item.slice(0, 30)}>{item}</li>
        ))}
      </ul>

      <h3 className="text-headline-sm">2. On the day</h3>
      <ul className="mt-1 list-disc pl-6">
        <li>
          <span className="font-semibold">Arrival time:</span> Please arrive around 15 minutes
          before your scheduled appointment in order to allow you time to go through the consent
          form and ask any questions you may have.
        </li>
        <li>
          <span className="font-semibold">Contact Info:</span> {contactInfoText(owner.phone)}
        </li>
      </ul>

      <h3 className="text-headline-sm">5. Post-Op Care</h3>
      <ul className="mt-1 list-disc pl-6">
        <li>
          <span className="font-semibold">Pick-Up Time:</span> {pickUpText(patient.name)}
        </li>
        <li>{afterProcedureText(patient.name)}</li>
        <li>{shavedAreaText(patient.name)}</li>
      </ul>

      <p className="mt-4 font-semibold">{COST_NOTE}</p>

      <p className="mt-4">{closingText(patient.name)}</p>

      <div className="mt-4">
        <p>Best wishes,</p>
        <p>The Snoots Team, North London</p>
      </div>
    </DocumentPreview>
  )
}

export const preSedationLetter = withDocumentEditor({
  id: 'pre-sedation-letter',
  label: 'Pre-Sedation Information Letter',
  // Sedation procedures are carried out at the Bounds Green clinic.
  clinic: SNOOTS_BG,
  defaultFields: defaultPreSedationLetterFields,
  Preview: PreSedationLetterForm,
  Pdf: PreSedationLetterPdf,
})
